// src/pages/LandingPage.js
import React from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CreditCardOffOutlinedIcon from "@mui/icons-material/CreditCardOffOutlined";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import WhyAsSection from "../components/WhyAsSection";
import WhoIsForSection from "../components/WhoIsForSection";
import Layout from "../components/Layout";
import WhatItDoes from "../components/WhatItDoes";

const HERO_IMAGE = "/vardion-high-resolution-logo-transparent.png";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { y: 24, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.6 } },
};

// Reusable glass look
const glass = {
  background: "rgba(255, 255, 255, 0.16)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(255, 255, 255, 0.35)",
  boxShadow: "0 20px 60px rgba(8, 40, 90, 0.25)",
};

// Full-bleed band (kept from your original technique)
const fullBleed = {
  width: "100vw",
  position: "relative",
  left: "50%",
  right: "50%",
  marginLeft: "-50vw",
  marginRight: "-50vw",
};

const darkBand = {
  ...fullBleed,
  background: "linear-gradient(135deg, #0b1f4d 0%, #123a8f 55%, #1769c2 100%)",
  color: "#fff",
};

function LandingPage({ onSignIn, onGuest }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Layout fullWidthSections={[1, 3]}>
      {/* ===== HERO ===== */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          py: isMobile ? 6 : 12,
          px: isMobile ? 2 : 0,
        }}
      >
        {/* Decorative blurred blobs */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -80,
            left: -80,
            width: 320,
            height: 320,
            borderRadius: "50%",
            background: "#7c5cff",
            opacity: 0.45,
            filter: "blur(90px)",
            pointerEvents: "none",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: -100,
            right: -60,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: "#00e5c0",
            opacity: 0.4,
            filter: "blur(100px)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Box
              sx={{
                ...glass,
                borderRadius: { xs: "24px", md: "36px" },
                p: { xs: 3, md: 7 },
                textAlign: "center",
              }}
            >
              <motion.div variants={itemVariants}>
                <Box mb={isMobile ? 2 : 3}>
                  <img
                    src={HERO_IMAGE}
                    alt={t("Vardion Logo")}
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      maxHeight: isMobile ? 110 : 170,
                      filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.25))",
                    }}
                  />
                </Box>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant={isMobile ? "h4" : "h2"}
                  component="h1"
                  sx={{
                    mb: 2,
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                    color: "#fff",
                    textShadow: "0 2px 18px rgba(8,40,90,0.35)",
                  }}
                >
                  {t("Stop dreading schedules. Start loving them.")}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  sx={{
                    mb: 4,
                    maxWidth: 680,
                    mx: "auto",
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.92)",
                  }}
                >
                  {t(
                    "Tired of messy calendars and endless back-and-forth? Vardion's got you covered. Build, share, and manage your weekly and monthly schedules with a ridiculously smart and easy-to-use tool that makes everyone happy.",
                  )}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Button
                    variant="contained"
                    startIcon={<GoogleIcon />}
                    onClick={onSignIn}
                    size="large"
                    sx={{
                      px: { xs: 4, sm: 5 },
                      py: 1.6,
                      width: { xs: "100%", sm: "auto" },
                      fontWeight: 700,
                      fontSize: isMobile ? "1rem" : "1.1rem",
                      borderRadius: "50px",
                      textTransform: "none",
                      color: "#0b1f4d",
                      background: "#fff",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                      transition: "transform .2s ease, box-shadow .2s ease",
                      "&:hover": {
                        background: "#f3f7ff",
                        transform: "translateY(-3px)",
                        boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    {t("Get Started for Free")}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={onGuest}
                    size="large"
                    sx={{
                      px: { xs: 4, sm: 5 },
                      py: 1.55,
                      width: { xs: "100%", sm: "auto" },
                      fontWeight: 600,
                      borderRadius: "50px",
                      textTransform: "none",
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.7)",
                      borderWidth: 2,
                      transition: "all .2s ease",
                      "&:hover": {
                        borderColor: "#fff",
                        borderWidth: 2,
                        background: "rgba(255,255,255,0.15)",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    {t("Try as guest (nothing is saved)")}
                  </Button>
                </Stack>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  justifyContent="center"
                  sx={{ mt: 4 }}
                >
                  {[
                    {
                      icon: <LockOutlinedIcon />,
                      label: t("Encrypted in your browser"),
                    },
                    {
                      icon: <BoltOutlinedIcon />,
                      label: t("Ready in seconds"),
                    },
                    {
                      icon: <CreditCardOffOutlinedIcon />,
                      label: t("Free, no card needed"),
                    },
                  ].map((c) => (
                    <Chip
                      key={c.label}
                      icon={c.icon}
                      label={c.label}
                      sx={{
                        color: "#fff",
                        fontWeight: 500,
                        background: "rgba(255,255,255,0.18)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        "& .MuiChip-icon": { color: "#fff" },
                      }}
                    />
                  ))}
                </Stack>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* ===== WHAT IT DOES ===== */}
      <Box sx={{ ...darkBand, py: isMobile ? 7 : 10 }}>
        <Container maxWidth="lg">
          <WhatItDoes theme={theme} t={t} />
        </Container>
      </Box>

      {/* ===== WHO IS FOR ===== */}
      <WhoIsForSection theme={theme} t={t} />

      {/* ===== WHY US ===== */}
      <Box sx={{ ...darkBand, py: isMobile ? 7 : 10 }}>
        <Container maxWidth="lg">
          <WhyAsSection theme={theme} t={t} />
        </Container>
      </Box>

      {/* ===== FINAL CTA ===== */}
      <Container
        maxWidth="md"
        sx={{ py: isMobile ? 6 : 10, px: isMobile ? 2 : 3 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              ...glass,
              borderRadius: { xs: "24px", md: "36px" },
              p: { xs: 4, md: 8 },
              textAlign: "center",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h3"}
              sx={{
                mb: 4,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: "#fff",
                textShadow: "0 2px 18px rgba(8,40,90,0.35)",
              }}
            >
              {t("Ready to take control of your schedules?")}
            </Typography>

            <Button
              variant="contained"
              startIcon={<GoogleIcon />}
              onClick={onSignIn}
              size="large"
              sx={{
                px: isMobile ? 4 : 7,
                py: 1.8,
                fontWeight: 800,
                fontSize: isMobile ? "1rem" : "1.25rem",
                borderRadius: "60px",
                textTransform: "none",
                color: "#0b1f4d",
                background: "#fff",
                boxShadow: "0 12px 34px rgba(0,0,0,0.3)",
                transition: "transform .2s ease, box-shadow .2s ease",
                "&:hover": {
                  background: "#f3f7ff",
                  transform: "translateY(-3px) scale(1.04)",
                  boxShadow: "0 18px 44px rgba(0,0,0,0.35)",
                },
              }}
            >
              {t("Start Now")}
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Layout>
  );
}

export default LandingPage;
